import { useForm } from "@inertiajs/react";
import { useEffect, useState } from "react";
import LogoUpload from "@/components/LogoUpload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { bizminer_admin_organizations_path } from "@/routes";

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

export function CreateOrganizationDialog({
  open,
  onOpenChange,
}: CreateOrganizationDialogProps) {
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const { data, setData, post, processing, errors, reset, transform } = useForm(
    {
      organization: {
        name: "",
        slug: "",
        website: "",
        description: "",
        logo: null as File | null,
      },
    },
  );

  useEffect(() => {
    if (!slugManuallyEdited && data.organization.name) {
      setData("organization.slug", generateSlug(data.organization.name));
    }
  }, [data.organization.name, slugManuallyEdited, setData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    transform((formData) => {
      const submitData: Record<string, unknown> = {
        organization: {
          name: formData.organization.name,
          slug: formData.organization.slug,
          website: formData.organization.website,
          description: formData.organization.description,
        },
      };

      if (formData.organization.logo) {
        (submitData.organization as Record<string, unknown>).logo =
          formData.organization.logo;
      }

      return submitData;
    });

    post(bizminer_admin_organizations_path(), {
      forceFormData: !!data.organization.logo,
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    reset();
    setSlugManuallyEdited(false);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true);
    const sanitizedSlug = e.target.value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setData("organization.slug", sanitizedSlug);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px] text-foreground">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Add a new organization to the platform.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="org-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="org-name"
                type="text"
                value={data.organization.name}
                onChange={(e) => setData("organization.name", e.target.value)}
                placeholder="Enter organization name"
                required
                autoFocus
              />
              {errors["organization.name"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.name"]}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-slug">
                Slug <span className="text-destructive">*</span>
              </Label>
              <Input
                id="org-slug"
                type="text"
                value={data.organization.slug}
                onChange={handleSlugChange}
                placeholder="organization-slug"
                required
              />
              <p className="text-xs text-muted-foreground">
                URL-friendly identifier. Auto-generated from name but can be
                customized.
              </p>
              {errors["organization.slug"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.slug"]}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-website">Website</Label>
              <Input
                id="org-website"
                type="url"
                value={data.organization.website}
                onChange={(e) =>
                  setData("organization.website", e.target.value)
                }
                placeholder="https://example.com"
              />
              {errors["organization.website"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.website"]}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                value={data.organization.description}
                onChange={(e) =>
                  setData("organization.description", e.target.value)
                }
                placeholder="Brief description of the organization"
                rows={3}
              />
              {errors["organization.description"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.description"]}
                </p>
              )}
            </div>

            <LogoUpload
              onLogoChange={(file) => setData("organization.logo", file)}
              error={errors["organization.logo"]}
              label="Logo (Optional)"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                processing || !data.organization.name || !data.organization.slug
              }
            >
              {processing ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
